import { Trans } from "@lingui/macro";
import cx from "classnames";
import { useMemo } from "react";

import { type BookRow, type MarketState, num, usd, utcClock } from "../terminalMock";

const HEAD_GRID = "grid grid-cols-3 px-14 py-9 text-11 tracking-[0.4px]";
const ROW_GRID = "relative grid cursor-pointer grid-cols-3 px-14 py-[4.5px] text-[12.5px]";

export type BookTab = "book" | "trades";

type Props = {
  market: MarketState;
  rows: number;
  activeTab: BookTab;
  onTabChange: (tab: BookTab) => void;
  className?: string;
  style?: React.CSSProperties;
};

type DepthRow = { price: string; size: string; total: string; width: string };

function toDepthRows(source: BookRow[], rows: number, maxSize: number): DepthRow[] {
  let cumulative = 0;

  return source.slice(0, rows).map((row) => {
    cumulative += row.s;

    return {
      price: num(row.p, 1),
      size: num(row.s, 5),
      total: num(cumulative, 5),
      width: `${((row.s / maxSize) * 100).toFixed(1)}%`,
    };
  });
}

export function OrderBookPanel({ market, rows, activeTab, onTabChange, className, style }: Props) {
  const { book, trades, price, open24 } = market;

  const changePct = ((price - open24) / open24) * 100;
  const isUp = changePct >= 0;

  const { asks, bids } = useMemo(() => {
    const maxSize = Math.max(...book.asks.concat(book.bids).map((row) => row.s));

    return {
      asks: toDepthRows(book.asks, rows, maxSize),
      bids: toDepthRows(book.bids, rows, maxSize),
    };
  }, [book, rows]);

  const tabs: { key: BookTab; label: React.ReactNode }[] = [
    { key: "book", label: <Trans>Order Book</Trans> },
    { key: "trades", label: <Trans>Trades</Trans> },
  ];

  return (
    <section className={cx("t-panel t-anim-scale-2 overflow-hidden", className)} style={style}>
      <div className="grid grid-cols-2">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onTabChange(tab.key)}
              className="border-b-2 py-13 text-[13.5px] font-semibold transition-all"
              style={
                isActive
                  ? { color: "var(--t-act)", background: "var(--t-panel3)", borderColor: "var(--t-act)" }
                  : { color: "var(--t-mut)", background: "transparent", borderColor: "var(--t-line)" }
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "book" && (
        <div>
          <div className={HEAD_GRID} style={{ background: "var(--t-panel2)", color: "var(--t-mut)" }}>
            <span>
              <Trans>Price</Trans>
            </span>
            <span className="text-right">
              <Trans>Size (BTC)</Trans>
            </span>
            <span className="text-right">
              <Trans>Total (BTC)</Trans>
            </span>
          </div>

          {asks.map((row, index) => (
            <div key={`ask-${index}`} className={cx(ROW_GRID, "t-ask-hover t-nums")}>
              <span
                className="absolute inset-y-0 right-0 transition-[width] duration-500 ease-out"
                style={{ width: row.width, background: "var(--t-depth-ask)" }}
              />
              <span className="relative font-semibold" style={{ color: "var(--t-short)" }}>
                {row.price}
              </span>
              <span className="relative text-right" style={{ color: "var(--t-tx2)" }}>
                {row.size}
              </span>
              <span className="relative text-right" style={{ color: "var(--t-mut)" }}>
                {row.total}
              </span>
            </div>
          ))}

          <div
            className="flex items-center justify-between border-y px-14 py-10"
            style={{ background: "var(--t-panel3)", borderColor: "var(--t-line)" }}
          >
            <span className="t-display t-nums text-24" style={{ color: "var(--t-deep)" }}>
              {usd(price)}
            </span>
            <span
              className="t-nums text-13 font-bold"
              style={{ color: isUp ? "var(--t-long)" : "var(--t-short)" }}
            >
              {isUp ? "+" : ""}
              {changePct.toFixed(2)}%
            </span>
          </div>

          {bids.map((row, index) => (
            <div key={`bid-${index}`} className={cx(ROW_GRID, "t-bid-hover t-nums")}>
              <span
                className="absolute inset-y-0 right-0 transition-[width] duration-500 ease-out"
                style={{ width: row.width, background: "var(--t-depth-bid)" }}
              />
              <span className="relative font-semibold" style={{ color: "var(--t-long)" }}>
                {row.price}
              </span>
              <span className="relative text-right" style={{ color: "var(--t-tx2)" }}>
                {row.size}
              </span>
              <span className="relative text-right" style={{ color: "var(--t-mut)" }}>
                {row.total}
              </span>
            </div>
          ))}
        </div>
      )}

      {activeTab === "trades" && (
        <div>
          <div className={HEAD_GRID} style={{ background: "var(--t-panel2)", color: "var(--t-mut)" }}>
            <span>
              <Trans>Price</Trans>
            </span>
            <span className="text-right">
              <Trans>Size (BTC)</Trans>
            </span>
            <span className="text-right">
              <Trans>Time</Trans>
            </span>
          </div>

          {trades.map((trade, index) => (
            <div
              key={`${trade.t}-${index}`}
              className="t-row-hover t-anim-sweep-fast t-nums grid grid-cols-3 px-14 py-5 text-[12.5px]"
            >
              <span className="font-semibold" style={{ color: trade.up ? "var(--t-long)" : "var(--t-short)" }}>
                {num(trade.p, 1)}
              </span>
              <span className="text-right" style={{ color: "var(--t-tx2)" }}>
                {num(trade.s, 4)}
              </span>
              <span className="text-right" style={{ color: "var(--t-mut2)" }}>
                {utcClock(trade.t)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
